import { useState } from 'react';
import { Highlighter, Square, ArrowUpRight, Type, EyeOff, Trash2 } from 'lucide-react';

export type AnnotationTool = 'highlight' | 'rectangle' | 'freehand' | 'text' | 'arrow' | 'redaction' | null;

interface AnnotationToolbarProps {
  activeTool: AnnotationTool;
  onToolChange: (tool: AnnotationTool) => void;
  activeColor: string;
  onColorChange: (color: string) => void;
  rectangleFill: boolean;
  onRectangleFillChange: (fill: boolean) => void;
  onClearAll?: () => void;
  annotationCount?: number;
  compact?: boolean;
  canRedact?: boolean;
}

const COLORS = [
  { value: '#FFEB3B', label: 'Yellow' },
  { value: '#EF4444', label: 'Red' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#22C55E', label: 'Green' },
  { value: '#F97316', label: 'Orange' },
];

const TOOLS: { tool: AnnotationTool; icon: typeof Square; label: string; shortcut: string }[] = [
  { tool: 'highlight', icon: Highlighter, label: 'Highlight', shortcut: 'H' },
  { tool: 'rectangle', icon: Square, label: 'Rectangle', shortcut: 'R' },
  { tool: 'text', icon: Type, label: 'Text Note', shortcut: 'T' },
  { tool: 'arrow', icon: ArrowUpRight, label: 'Arrow', shortcut: 'A' },
];

const REDACT_TOOL = { tool: 'redaction' as AnnotationTool, icon: EyeOff, label: 'Redact', shortcut: 'X' };

export default function AnnotationToolbar({
  activeTool,
  onToolChange,
  activeColor,
  onColorChange,
  rectangleFill,
  onRectangleFillChange,
  onClearAll,
  annotationCount = 0,
  compact = false,
  canRedact = false,
}: AnnotationToolbarProps) {
  const [showColors, setShowColors] = useState(false);

  const allTools = canRedact ? [...TOOLS, REDACT_TOOL] : TOOLS;

  return (
    <div className={`flex items-center gap-1 ${compact ? '' : 'bg-white border border-gray-200 rounded-lg p-1 shadow-sm'}`}>
      {allTools.map(({ tool, icon: Icon, label, shortcut }) => (
        <button
          key={tool}
          onClick={() => onToolChange(activeTool === tool ? null : tool)}
          className={`p-2 rounded-md transition-colors ${
            activeTool === tool
              ? tool === 'redaction' ? 'bg-red-700 text-white' : 'bg-primary-600 text-white'
              : tool === 'redaction' ? 'text-red-600 hover:bg-red-50' : 'text-gray-600 hover:bg-gray-100'
          }`}
          title={`${label} (${shortcut})`}
        >
          <Icon className="w-4 h-4" />
        </button>
      ))}

      {/* Redaction mode indicator */}
      {activeTool === 'redaction' && (
        <span className="ml-1 text-xs font-semibold text-red-700 bg-red-100 px-2 py-1 rounded">
          REDACT MODE
        </span>
      )}

      {/* Fill/Outline toggle for rectangle */}
      {activeTool === 'rectangle' && (
        <button
          onClick={() => onRectangleFillChange(!rectangleFill)}
          className={`ml-1 p-2 rounded-md transition-colors text-xs font-medium ${
            rectangleFill
              ? 'bg-primary-100 text-primary-700 border border-primary-300'
              : 'text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
          title={rectangleFill ? 'Switch to outline' : 'Switch to fill'}
        >
          <div className="w-4 h-4 rounded-sm" style={{
            backgroundColor: rectangleFill ? activeColor + '99' : 'transparent',
            border: `2px solid ${activeColor}`,
          }} />
        </button>
      )}

      {/* Color picker - hidden in redact mode */}
      {activeTool !== 'redaction' && (
      <div className="relative ml-1">
        <button
          onClick={() => setShowColors(!showColors)}
          className="w-7 h-7 rounded-md border-2 border-gray-300 hover:border-gray-400 transition-colors"
          style={{ backgroundColor: activeColor }}
          title="Annotation color"
        />
        {showColors && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg p-2 shadow-lg z-50 flex gap-1">
            {COLORS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => { onColorChange(value); setShowColors(false); }}
                className={`w-6 h-6 rounded-full border-2 transition-all ${
                  activeColor === value ? 'border-gray-800 scale-110' : 'border-gray-300 hover:border-gray-500'
                }`}
                style={{ backgroundColor: value }}
                title={label}
              />
            ))}
          </div>
        )}
      </div>
      )}

      {/* Clear all */}
      {onClearAll && annotationCount > 0 && (
        <button
          onClick={onClearAll}
          className="ml-1 p-2 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
          title={`Clear all annotations (${annotationCount})`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}

      {/* Count badge */}
      {annotationCount > 0 && (
        <span className="ml-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
          {annotationCount}
        </span>
      )}
    </div>
  );
}
